"use client";

import type { PropsWithChildren } from "react";

import { AudioListProvider, useAudioList } from "@/app/_providers/audio-list";
import { AudioListItem, AudioListItemContent, AudioListItemHeader } from "@components/core/AudioListItem";
import { PlayPauseButton } from "@components/core/PlayPauseButton";
import { useWavesurfer } from "@ui/hooks/useWavesurferPlayer";
import { formatTime } from "@ui/lib/time";
import { cn } from "@ui/lib/utils";
import React from "react";

export interface IAudioPlayerProps {
  description?: string;
  durationClassName?: string;
  endAligned?: boolean;
  hideDuration?: boolean;
  hideWaveformOnMobile?: boolean;
  hoverEffects?: boolean;
  id: string;
  onPlay?: () => void;
  ref?: React.Ref<HTMLDivElement>;
  sampleUrl: string;
  title?: string;
}

export const AudioItem = ({
  id,
  title,
  description,
  sampleUrl,
  children,
  ref,
  hideDuration,
  hoverEffects = true,
  endAligned = true,
  hideWaveformOnMobile = true,
  durationClassName,
  onPlay,
}: PropsWithChildren<IAudioPlayerProps>) => {
  const timelineRef = React.useRef<HTMLDivElement>(null);

  const { duration, isPlaying, togglePlayPause, keyboardContainerRef } = useWavesurfer({
    url: sampleUrl,
    timelineRef,
    id,
  });
  const { setPlayingItemId, playingItemId } = useAudioList();

  const onPlayRef = React.useRef(onPlay);
  React.useEffect(() => {
    onPlayRef.current = onPlay;
  }, [onPlay]);
  const wasPlayingRef = React.useRef(false);
  React.useEffect(() => {
    if (isPlaying && !wasPlayingRef.current) onPlayRef.current?.();
    wasPlayingRef.current = isPlaying;
  }, [isPlaying]);

  return (
    <AudioListItem
      aria-label={`Audio Player ${title ? `- ${title}` : ""}`}
      hoverEffects={hoverEffects}
      onClick={() => {
        setPlayingItemId(playingItemId === id ? null : id);
      }}
      ref={keyboardContainerRef}
      tabIndex={-1}
    >
      <AudioListItemContent endAligned={endAligned}>
        <PlayPauseButton isPlaying={isPlaying} onClick={togglePlayPause} />
        {(title || description) && (
          <AudioListItemHeader>
            <h4 className='f-ui-03'>{title}</h4>
            {description && (
              <h4 className='f-ui-05 line-clamp-2' title={description}>
                {description}
              </h4>
            )}
          </AudioListItemHeader>
        )}
        <div
          className={cn(
            "flex items-center gap-20",
            endAligned && "justify-end",
            hideWaveformOnMobile ? "lg:flex-auto" : "flex-auto",
          )}
        >
          {!hideDuration && <time className={cn("f-ui-05 text-light", durationClassName)}>{formatTime(duration)}</time>}
          <div className='flex h-32 flex-grow'>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
              className={cn("w-full", hideWaveformOnMobile && "hidden lg:block")}
              onClick={event => event.stopPropagation()}
              ref={timelineRef}
            />
          </div>
        </div>
      </AudioListItemContent>
      {children}
    </AudioListItem>
  );
};

interface AudioListProps extends React.HTMLAttributes<HTMLUListElement> {
  heading?: null | string;
  ref?: React.Ref<HTMLUListElement>;
}

export const AudioList = ({ children, className, ref, heading, ...props }: AudioListProps) => {
  return (
    <div className='w-full'>
      {heading && <h3 className='f-ui-03 border-b px-12 py-12'>{heading}</h3>}
      <ul className={cn("w-full divide-y rounded-lg border", className)} ref={ref} {...props}>
        <AudioListProvider>{children}</AudioListProvider>
      </ul>
    </div>
  );
};
